/**
 * Mobile-only swipeable multi-image carousel on product cards (collection pages).
 *
 * 1) Swipe vs tap: a click that follows a pointer drag of more than a few pixels is suppressed, so swiping through
 *    photos never navigates to the product page — only a clean tap does.
 * 2) Dot pagination: synced to the currently visible slide via IntersectionObserver (same technique as the product
 *    page's story carousel).
 *
 * Works for cards rendered on initial page load as well as cards injected later (facet filtering, pagination) via
 * event delegation (swipe/tap) and a MutationObserver (dot wiring).
 */
(() => {
  let startX = 0;
  let startY = 0;
  let moved = false;
  let activeLink = null;

  function onPointerDown(event) {
    const link = event.target.closest('[data-product-card-swipe-link]');
    if (!link) return;
    activeLink = link;
    moved = false;
    startX = event.clientX;
    startY = event.clientY;
  }

  function onPointerMove(event) {
    if (!activeLink) return;
    if (Math.abs(event.clientX - startX) > 8 || Math.abs(event.clientY - startY) > 8) {
      moved = true;
    }
  }

  function onPointerUp() {
    activeLink = null;
  }

  function onClick(event) {
    const link = event.target.closest('[data-product-card-swipe-link]');
    if (!link) return;

    if (moved) {
      event.preventDefault();
    }

    moved = false;
  }

  document.addEventListener('pointerdown', onPointerDown, { passive: true });
  document.addEventListener('pointermove', onPointerMove, { passive: true });
  document.addEventListener('pointerup', onPointerUp, { passive: true });
  document.addEventListener('click', onClick, true);

  // --------------------------------------------------------------------------------------------------------------
  // Dot pagination sync
  // --------------------------------------------------------------------------------------------------------------

  function wireCarousel(list) {
    if (list.dataset.cardCarouselInit || !('IntersectionObserver' in window)) return;
    list.dataset.cardCarouselInit = 'true';

    const items = Array.from(list.children);
    const dotsWrapper = list.parentElement.querySelector('.product-card__mobile-carousel-dots');
    const dots = dotsWrapper ? Array.from(dotsWrapper.children) : [];
    if (dots.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = items.indexOf(entry.target);

          dots.forEach((dot, dotIndex) => {
            if (dotIndex === index) {
              dot.setAttribute('aria-current', 'true');
            } else {
              dot.removeAttribute('aria-current');
            }
          });
        });
      },
      { root: list, threshold: 0.6 }
    );

    items.forEach((item) => observer.observe(item));
  }

  function wireAll(root) {
    root.querySelectorAll('[data-product-card-carousel-list]').forEach(wireCarousel);
  }

  wireAll(document);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;

        if (node.matches?.('[data-product-card-carousel-list]')) wireCarousel(node);
        wireAll(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
